import { useEffect, useRef, useState, FormEvent } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "react-query";
import { useAuth } from "@/hooks/useAuth";
import { agentEligibilityApi } from "@/services/agentApi";
import {
  EligibilityResult,
  EligibilityReviewResponse,
} from "../../../../shared/src/eligibility/EligibilityResult";
import {
  eligibilityCategories,
  eligibilityDisclaimer,
  eligibilityFields,
  inputValues,
  parseEligibilityInputs,
  outcomeLabel,
} from "../../../../shared/src/eligibility/eligibilityTypes";
import type {
  EligibilityCheck,
  EligibilityCourse,
} from "../../../../shared/src/eligibility/eligibilityApi";

const errorMessage = (error: unknown) =>
  (error as any)?.response?.data?.error ||
  (error as Error)?.message ||
  "Unable to complete this request. Please try again.";
const positiveId = (value: string | undefined | null) =>
  value && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value))
    ? Number(value)
    : null;
const categoryLabels = {
  academic: "Academic qualifications",
  english: "English-language qualification",
  experience: "Study gap, experience and programme information",
  programme: "Programme-specific information",
  intake: "Intake",
};
function CourseSummary({ course }: { course: EligibilityCourse }) {
  return (
    <section className="eligibility-panel eligibility-course-summary">
      <span className="eligibility-eyebrow">Selected programme</span>
      <h2>{course.programmeName}</h2>
      <p>{course.institutionName}</p>
      <p className="small text-muted mb-0">
        {course.startDate ||
          course.intakeDates ||
          "Confirm intake with the institution."}
      </p>
    </section>
  );
}
function Retry({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className="eligibility-panel" role="alert">
      <p>{errorMessage(error)}</p>
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        onClick={retry}
      >
        Retry
      </button>
    </div>
  );
}
function SavedResult({
  check,
  refresh,
}: {
  check: EligibilityCheck;
  refresh: () => void;
}) {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const manualMet =
    check.review?.status === "DECIDED" &&
    check.review.decisionOutcome === "CRITERIA_MET";
  const canProceed =
    check.review?.status === "DECIDED"
      ? manualMet
      : check.result.outcome === "CRITERIA_MET";
  async function action(kind: "handoff" | "review") {
    setBusy(true);
    setError("");
    try {
      if (kind === "handoff") {
        const result = await agentEligibilityApi.handoff(check.id);
        if (!alive.current) return;
        // Accept only the existing ID-based application route; the server rechecks permissions.
        if (
          !/^\/applications\/new\?universityId=[1-9]\d*&offeringId=[1-9]\d*$/.test(
            result.url,
          )
        )
          throw new Error(
            "Invalid application destination. Please run a new check.",
          );
        navigate(result.url);
      } else {
        await agentEligibilityApi.requestReview(check.id, message.trim());
        if (alive.current) refresh();
      }
    } catch (err) {
      if (alive.current) setError(errorMessage(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  return (
    <div className="eligibility-saved-result-grid">
      <aside
        className="eligibility-review-sidebar"
        aria-label="Eligibility review and next action"
      >
        <EligibilityReviewResponse check={check} />
        <section className="eligibility-panel eligibility-next-step">
          <h2>Next step</h2>
          <p className="small text-muted">
            Current course access, institution agreements and criteria are
            checked again before opening a new application. This check does not
            change an existing draft.
          </p>
          {error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}
          {canProceed && (
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => action("handoff")}
            >
              {busy ? "Checking current access…" : "Proceed to Application"}
            </button>
          )}
          {!check.review && !canProceed && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                action("review");
              }}
            >
              <label htmlFor="eligibility-review-message">
                What would you like admissions to review?
              </label>
              <textarea
                id="eligibility-review-message"
                className="form-control mb-3"
                maxLength={2000}
                required
                rows={3}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
              <button
                className="btn btn-primary"
                disabled={busy || !message.trim()}
              >
                {busy ? "Submitting review…" : "Request Eligibility Review"}
              </button>
            </form>
          )}
          {check.review && !canProceed && (
            <p>
              Your review status and any response are shown above. Start a new
              check if student information has changed.
            </p>
          )}
          <Link
            className="d-block mt-3"
            to={`/eligibility?offeringId=${check.offeringId}`}
          >
            Run a new check for this programme
          </Link>
          <p className="eligibility-disclaimer">{eligibilityDisclaimer}</p>
        </section>
      </aside>
      <div className="eligibility-supporting-details">
        <CourseSummary course={check.courseSnapshot} />
        <EligibilityResult check={check} showInputs showReview={false} />
      </div>
    </div>
  );
}
function NewCheck({
  offeringId,
  accountId,
}: {
  offeringId: number;
  accountId: string;
}) {
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<string, string>>({});
  const [intake, setIntake] = useState("");
  const [studentId, setStudentId] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [studentPage, setStudentPage] = useState(1);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const context = useQuery(
    ["eligibility-context", accountId, offeringId],
    ({ signal }) => agentEligibilityApi.context(offeringId, signal),
    { enabled: !!accountId },
  );
  useEffect(() => {
    setIntake((current) =>
      context.data?.intakes.includes(current) ? current : "",
    );
  }, [context.data?.intakes]);
  const students = useQuery(
    ["eligibility-students", accountId, studentSearch, studentPage],
    ({ signal }) =>
      agentEligibilityApi.students(studentSearch, studentPage, signal),
    { enabled: !!accountId },
  );
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    setValues({});
    if (!studentId) {
      setLoadingPrefill(false);
      return () => controller.abort();
    }
    setLoadingPrefill(true);
    agentEligibilityApi
      .prefill(Number(studentId), controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setValues(inputValues(result.inputs));
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(err));
          setStudentId("");
          setSelectedName("");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingPrefill(false);
      });
    return () => controller.abort();
  }, [studentId]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      if (!context.data?.intakes.includes(intake))
        throw new Error(
          "Select a current catalogue intake. Ask an administrator to maintain missing intakes.",
        );
      const inputs = parseEligibilityInputs(values);
      setBusy(true);
      const check = await agentEligibilityApi.createCheck({
        offeringId,
        intake: intake.trim(),
        inputs,
        ...(studentId ? { studentProfileId: Number(studentId) } : {}),
      });
      if (alive.current) navigate(`/eligibility/checks/${check.id}`);
    } catch (err) {
      if (alive.current) setError(errorMessage(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  if (context.isLoading)
    return <div role="status">Loading programme and criteria…</div>;
  if (context.isError)
    return <Retry error={context.error} retry={() => context.refetch()} />;
  if (!context.data) return null;
  const data = context.data;
  return (
    <div className="eligibility-grid eligibility-check-grid">
      <div>
        <CourseSummary course={data.course} />
        <form
          className="eligibility-panel eligibility-check-form"
          onSubmit={submit}
        >
          <fieldset disabled={busy}>
            <legend>
              <span className="eligibility-section-marker" aria-hidden="true">
                1
              </span>
              Student details
            </legend>
            <label htmlFor="eligibility-student-search">
              Find a saved student (optional)
            </label>
            <input
              id="eligibility-student-search"
              className="form-control mb-2"
              placeholder="Search by name"
              maxLength={200}
              value={studentSearch}
              onChange={(event) => {
                setStudentSearch(event.target.value);
                setStudentPage(1);
              }}
            />
            <label htmlFor="eligibility-student">Saved student</label>
            <select
              id="eligibility-student"
              className="form-select"
              value={studentId}
              disabled={students.isLoading || students.isError}
              onChange={(event) => {
                setStudentId(event.target.value);
                const student = students.data?.items.find(
                  (item) => String(item.id) === event.target.value,
                );
                setSelectedName(
                  student ? `${student.firstName} ${student.lastName}` : "",
                );
              }}
            >
              <option value="">Enter details manually</option>
              {studentId &&
                !students.data?.items.some(
                  (student) => String(student.id) === studentId,
                ) && <option value={studentId}>{selectedName}</option>}
              {!students.isError &&
                students.data?.items.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
            </select>
            {students.isError && (
              <p role="alert" className="small">
                Student profiles could not be loaded.{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={() => students.refetch()}
                >
                  Retry
                </button>
              </p>
            )}
            {students.data && students.data.total > 25 && (
              <nav
                aria-label="Saved student pages"
                className="eligibility-actions mt-2"
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={studentPage === 1 || students.isFetching}
                  onClick={() => setStudentPage((page) => page - 1)}
                >
                  Previous students
                </button>
                <span>Page {studentPage}</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={
                    studentPage * 25 >= students.data.total ||
                    students.isFetching
                  }
                  onClick={() => setStudentPage((page) => page + 1)}
                >
                  Next students
                </button>
              </nav>
            )}
            <p className="small text-muted mt-2 mb-0">
              Confirm prefilled facts. Original scales and test versions are
              preserved; missing information stays blank.
            </p>
          </fieldset>
          {loadingPrefill && <p role="status">Loading selected student…</p>}
          <fieldset disabled={busy || loadingPrefill}>
            <legend>
              <span className="eligibility-section-marker" aria-hidden="true">
                2
              </span>
              Intake
            </legend>
            <label htmlFor="eligibility-intake">Intake to assess</label>
            <select
              id="eligibility-intake"
              className="form-select"
              required
              disabled={!data.intakes.length || context.isFetching}
              value={intake}
              onChange={(event) => setIntake(event.target.value)}
            >
              <option value="">Select an intake</option>
              {data.intakes.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {!data.intakes.length && (
              <p className="small text-muted mt-2" role="status">
                No catalogue intakes are available. Ask an administrator to
                maintain this offering’s start dates or its programme intake
                dates before checking eligibility.
              </p>
            )}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-2"
              disabled={context.isFetching}
              onClick={() => context.refetch()}
            >
              Refresh catalogue intakes
            </button>
          </fieldset>
          {eligibilityCategories
            .filter(
              (category) => category !== "intake" && category !== "programme",
            )
            .map((category, index) => (
              <fieldset key={category} disabled={busy || loadingPrefill}>
                <legend>
                  <span
                    className="eligibility-section-marker"
                    aria-hidden="true"
                  >
                    {index + 3}
                  </span>
                  {categoryLabels[category]}
                </legend>
                <div className="eligibility-form-grid">
                  {eligibilityFields
                    .filter(
                      (spec) =>
                        spec.category === category ||
                        (category === "experience" &&
                          spec.category === "programme"),
                    )
                    .map((spec) => (
                      <div
                        key={spec.field}
                        className={
                          spec.type === "textarea"
                            ? "eligibility-wide-field"
                            : undefined
                        }
                      >
                        <label htmlFor={`eligibility-${spec.field}`}>
                          {spec.label}
                        </label>
                        {spec.type === "textarea" ? (
                          <textarea
                            id={`eligibility-${spec.field}`}
                            className="form-control"
                            maxLength={2000}
                            rows={3}
                            value={values[spec.field] || ""}
                            onChange={(event) =>
                              setValues((current) => ({
                                ...current,
                                [spec.field]: event.target.value,
                              }))
                            }
                          />
                        ) : (
                          <input
                            id={`eligibility-${spec.field}`}
                            className="form-control"
                            type={
                              spec.type === "integer" ? "number" : spec.type
                            }
                            min={
                              spec.type === "number" || spec.type === "integer"
                                ? 0
                                : undefined
                            }
                            max={
                              spec.type === "number" || spec.type === "integer"
                                ? 100000
                                : undefined
                            }
                            step={spec.type === "integer" ? 1 : "any"}
                            maxLength={200}
                            value={values[spec.field] || ""}
                            onChange={(event) =>
                              setValues((current) => ({
                                ...current,
                                [spec.field]: event.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    ))}
                </div>
              </fieldset>
            ))}
          {error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}
          <button
            className="btn btn-primary"
            disabled={
              busy ||
              loadingPrefill ||
              context.isFetching ||
              context.isError ||
              !data.intakes.includes(intake)
            }
          >
            {busy ? "Saving check…" : "Save and check eligibility"}
          </button>
          <p className="eligibility-disclaimer">{eligibilityDisclaimer}</p>
        </form>
      </div>
      <aside>
        <section className="eligibility-panel">
          <h2>How this check works</h2>
          <p>
            Each saved check compares your information with approved criteria
            for this programme and intake.
          </p>
          <p>
            Incomplete or unapproved criteria require admissions review. Leaving
            a field blank does not mean its requirement is waived.
          </p>
          <div className="eligibility-notice">
            <strong>
              {data.criteria.length
                ? "Approved criteria available"
                : "Criteria awaiting approval"}
            </strong>
            <p className="small">
              {data.criteria.length
                ? "The selected intake and effective dates determine which version applies."
                : "You can save your information and request an eligibility review."}
            </p>
          </div>
        </section>
        <section className="eligibility-panel">
          <h2>Institution access</h2>
          <p>{data.course.availability}</p>
          <p className="small text-muted mb-0">
            Student eligibility and your agency’s permission to apply are
            assessed separately.
          </p>
        </section>
      </aside>
    </div>
  );
}
function SavedChecks({ accountId }: { accountId: string }) {
  const [page, setPage] = useState(1);
  const query = useQuery(
    ["eligibility-checks", accountId, page],
    ({ signal }) => agentEligibilityApi.checks(page, signal),
    { enabled: !!accountId },
  );
  if (query.isLoading) return <div role="status">Loading saved checks…</div>;
  if (query.isError)
    return <Retry error={query.error} retry={() => query.refetch()} />;
  return (
    <section className="eligibility-panel eligibility-saved-checks">
      <h2>Saved checks</h2>
      {!query.data?.items.length && (
        <p>
          No saved eligibility checks yet. Choose a programme in{" "}
          <Link to="/courses">Course Search</Link> to begin.
        </p>
      )}
      <ul className="eligibility-criteria">
        {query.data?.items.map((check) => (
          <li key={check.id}>
            <div>
              <Link to={`/eligibility/checks/${check.id}`}>
                {check.courseSnapshot.programmeName}
              </Link>
              <p>
                {check.courseSnapshot.institutionName} · {check.intake}
              </p>
              <span className="small text-muted">
                {new Date(check.createdAt).toLocaleString()}
              </span>
            </div>
            <span className="eligibility-state">
              {check.review?.status === "DECIDED"
                ? "Review response received"
                : outcomeLabel[check.result.outcome]}
            </span>
          </li>
        ))}
      </ul>
      {query.data && query.data.total > 25 && (
        <nav
          aria-label="Eligibility check pages"
          className="eligibility-actions"
        >
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page === 1}
            onClick={() => setPage((value) => value - 1)}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page * 25 >= query.data.total}
            onClick={() => setPage((value) => value + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
function EligibilityWorkspace({ accountId }: { accountId: string }) {
  const [params] = useSearchParams();
  const { checkId } = useParams();
  const offeringId = positiveId(params.get("offeringId"));
  const id = positiveId(checkId);
  const invalid =
    (checkId !== undefined && !id) ||
    params.getAll("offeringId").length > 1 ||
    (params.has("offeringId") && !offeringId);
  const query = useQuery(
    ["eligibility-check", accountId, id],
    ({ signal }) => agentEligibilityApi.check(id!, signal),
    { enabled: !!accountId && !!id && !invalid },
  );
  return (
    <div className="eligibility-workspace eligibility-page eligibility-agent">
      <header className="eligibility-page-header">
        <div className="eligibility-actions eligibility-page-navigation">
          <Link to={offeringId ? `/courses/${offeringId}` : "/courses"}>
            Back to courses
          </Link>
          <Link to="/eligibility">Saved checks</Link>
        </div>
        <h1>Check Student Eligibility</h1>
        <p className="text-muted">
          Preliminary programme guidance before starting an application.
        </p>
      </header>
      {invalid ? (
        <div role="alert" className="eligibility-panel">
          Invalid selection. Choose a programme from{" "}
          <Link to="/courses">Course Search</Link>.
        </div>
      ) : id ? (
        query.isLoading ? (
          <div role="status">Loading saved check…</div>
        ) : query.isError ? (
          <Retry error={query.error} retry={() => query.refetch()} />
        ) : query.data ? (
          <SavedResult
            key={query.data.id}
            check={query.data}
            refresh={() => query.refetch()}
          />
        ) : null
      ) : offeringId ? (
        <NewCheck
          key={`${accountId}:${offeringId}`}
          offeringId={offeringId}
          accountId={accountId}
        />
      ) : (
        <SavedChecks accountId={accountId} />
      )}
    </div>
  );
}
export default function EligibilityPage() {
  const { user } = useAuth();
  return user ? (
    <EligibilityWorkspace key={user.id} accountId={user.id} />
  ) : null;
}
